import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDate, isoToLocalInput } from "./adminUtils";

export function OfferDialog({ open, onOpenChange, offer, onSaved }) {
  const [enabled, setEnabled] = useState(false);
  const [price, setPrice] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [total, setTotal] = useState("");
  const [remaining, setRemaining] = useState("");
  const [plan, setPlan] = useState("smallbiz");
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const o = offer || {};
    setEnabled(!!o.enabled);
    setPrice(String(o.price ?? 99));
    setEndsAt(isoToLocalInput(o.ends_at));
    setTotal(String(o.spots_total ?? 100));
    setRemaining(String(o.spots_left ?? o.spots_total ?? 100));
    setPlan(o.plan || "smallbiz");
    api.get("/plans").then((r) => setPlans(r.data || [])).catch(() => {});
  }, [open, offer]);

  // Paid plans only — a lifetime deal on the free plan makes no sense.
  const paidPlans = plans.filter((p) => p.key !== "personal");
  const selectedPlan = plans.find((p) => p.key === plan);
  const selectedName = selectedPlan?.name || offer?.plan_name || "Team";
  const selectedRegular = selectedPlan?.price ?? offer?.regular_price;

  const applyPreset = (hours) => {
    const d = new Date(Date.now() + hours * 3600 * 1000);
    setEndsAt(isoToLocalInput(d.toISOString()));
  };

  const save = async () => {
    const t = parseInt(total, 10);
    const r = parseInt(remaining, 10);
    const p = parseFloat(price);
    if (isNaN(t) || t <= 0) { toast.error("Total licenses must be a positive number."); return; }
    if (isNaN(r) || r < 0) { toast.error("Licenses remaining must be 0 or more."); return; }
    if (r > t) { toast.error("Remaining cannot exceed total."); return; }
    if (isNaN(p) || p <= 0) { toast.error("Price must be a positive number."); return; }
    const claimed = Math.max(0, t - r);
    setSaving(true);
    try {
      const res = await api.post("/admin/offer", {
        enabled,
        price: p,
        ends_at: endsAt ? new Date(endsAt).toISOString() : "",
        spots_total: t,
        spots_claimed: claimed,
        plan,
      });
      onSaved(res.data);
      toast.success(enabled ? "Lifetime offer is live" : "Lifetime offer saved (off)");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save offer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Ticket size={18} className="text-amber-400" /> Lifetime offer
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Run a limited-time one-time deal on the {selectedName} plan. Pick the plan, price, deadline and how many licenses are left. When live, it shows on the landing and billing pages.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 p-3">
            <div>
              <div className="text-sm font-medium">Offer live</div>
              <div className="text-xs text-zinc-500">Toggle the deal on or off instantly.</div>
            </div>
            <Switch data-testid="offer-enabled-switch" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Plan the lifetime deal grants</label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="offer-plan-select" className="bg-black border-zinc-800 focus:ring-amber-500">
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {paidPlans.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`offer-plan-option-${p.key}`}>
                    {p.name} — ${p.price}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-zinc-500 mt-1">Buyers get {selectedName}-tier limits forever for one payment. Free plan is excluded.</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">One-time price (USD)</label>
              <Input
                data-testid="offer-price-input"
                type="number" min="1" step="1"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="bg-black border-zinc-800 focus-visible:ring-amber-500 font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Regular price</label>
              <div className="h-10 flex items-center px-3 rounded-md border border-zinc-800 bg-black/40 font-mono text-sm text-zinc-400">
                ${selectedRegular ?? "—"}/mo
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Offer ends at</label>
            <Input
              data-testid="offer-ends-input"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="bg-black border-zinc-800 focus-visible:ring-amber-500 font-mono [color-scheme:dark]"
            />
            <div className="flex items-center gap-2 mt-2">
              {[["24h", 24], ["3 days", 72], ["7 days", 168]].map(([label, hrs]) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`offer-preset-${hrs}`}
                  onClick={() => applyPreset(hrs)}
                  className="text-xs rounded-md px-2.5 py-1 border border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-400 transition-colors"
                >
                  +{label}
                </button>
              ))}
              <button
                type="button"
                data-testid="offer-preset-clear"
                onClick={() => setEndsAt("")}
                className="text-xs rounded-md px-2.5 py-1 border border-zinc-800 text-zinc-500 hover:border-zinc-600 transition-colors ml-auto"
              >
                No deadline
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Total licenses</label>
              <Input
                data-testid="offer-total-input"
                type="number" min="1" step="1"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                className="bg-black border-zinc-800 focus-visible:ring-amber-500 font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Licenses remaining</label>
              <Input
                data-testid="offer-remaining-input"
                type="number" min="0" step="1"
                value={remaining}
                onChange={(e) => setRemaining(e.target.value)}
                className="bg-black border-zinc-800 focus-visible:ring-amber-500 font-mono"
              />
            </div>
          </div>

          <div className="rounded-md bg-amber-500/5 border border-amber-500/15 p-2.5 text-xs text-zinc-400" data-testid="offer-preview">
            Visitors will see: <span className="text-amber-300 font-medium">{selectedName} Lifetime for ${price || "—"} once</span>
            {" · "}<span className="text-red-400">Only {remaining || 0} of {total || 0} licenses left</span>
            {endsAt && <> · ends {fmtDate(new Date(endsAt).toISOString())}</>}
            <div className="mt-1 text-zinc-500">
              Status: {enabled
                ? <span className="text-emerald-400">will be ON</span>
                : <span className="text-zinc-400">OFF</span>}
              {" "}(auto-hides when the deadline passes or 0 remain)
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-white/10 hover:bg-zinc-800" data-testid="offer-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-black font-medium" data-testid="offer-save">
            {saving ? "Saving…" : "Save offer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
